import { AccredibleButtonComponentModule } from '@accredible-frontend-v2/new-components/button';
import { AccredibleIconComponentModule } from '@accredible-frontend-v2/new-components/icon';
import {
  AccredibleSelectInputComponentModule,
  SelectInputOption,
} from '@accredible-frontend-v2/new-components/select-input';
import {
  MobilePopUpComponent,
  MobilePopUpComponentModule,
  MobilePopUpService,
} from '@accredible-frontend-v2/new-components/ui-common';
import {
  AccredibleLanguageService,
  AccredibleLanguageServiceModule,
} from '@accredible-frontend-v2/services/language';
import { AccredibleKey } from '@accredible-frontend-v2/utils/key-enum';
import {
  AccredibleResponsiveBreakpoint,
  AccredibleResponsiveHelper,
} from '@accredible-frontend-v2/utils/responsive-helper';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { map, tap } from 'rxjs';

const TRANSLATION_KEY = 'dod-directory-sorting.sorting_options';

@Component({
  standalone: true,
  selector: 'dod-directory-sorting',
  templateUrl: `./directory-sorting.component.html`,
  styleUrls: ['./directory-sorting.component.scss'],
  imports: [
    TranslocoModule,
    AccredibleIconComponentModule,
    AccredibleButtonComponentModule,
    MobilePopUpComponentModule,
    AccredibleSelectInputComponentModule,
    AccredibleLanguageServiceModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'onResize()',
  },
})
export class DirectorySortingComponent implements OnInit {
  private readonly _document = inject(DOCUMENT);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _mobilePopUpService = inject(MobilePopUpService);
  private readonly _viewContainerRef = inject(ViewContainerRef);

  // TODO: cannot switch to viewChild signal due to this ng-mocks bug: https://github.com/help-me-mom/ng-mocks/issues/8634
  @ViewChild(MobilePopUpComponent)
  private readonly _mobilePopUp: MobilePopUpComponent;

  sorting = input<number>(0);

  mobilePopUpVisible = signal<boolean>(false);

  onSortingChanged = output<{ index: number; skip: boolean }>();

  isMobile = signal<boolean>(false);
  selectedValue = signal<string>('');
  options = signal<SelectInputOption[]>([]);

  sortingOptionsControl = new FormControl<number>(this.sorting());

  constructor() {
    effect(
      () => {
        if (this.sorting() !== this.sortingOptionsControl.value) {
          this.sortChanged(this.sorting(), true);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this._detectMobileVersion();

    this._language
      .selectTranslateObject(TRANSLATION_KEY)
      .pipe(
        map((translations) =>
          (translations as unknown as string[]).map((name, index) => ({ name, value: index })),
        ),
        tap((options) => {
          if (options.length && !this.sortingOptionsControl.value) {
            this.options.set(options);
            this.selectedValue.set(options[0].name);
          }
        }),
      )
      .subscribe();
  }

  onResize(): void {
    this._detectMobileVersion();
  }

  sortChanged(index: any, skip = false): void {
    this.selectedValue.set(this.options()[index].name);
    this.sortingOptionsControl.setValue(index);

    this.onSortingChanged.emit({ index, skip });

    if (this.isMobile()) {
      this.closePopup();
    }
  }

  showPopup(): void {
    this._mobilePopUpService.setupPopUp(this._mobilePopUp, this._viewContainerRef);
    this._mobilePopUpService.open();
    this.mobilePopUpVisible.set(true);
  }

  closePopup(): void {
    this._mobilePopUp.closeMenu();
    this.mobilePopUpVisible.set(false);
  }

  onPopupClosed(): void {
    this.mobilePopUpVisible.set(false);
  }

  onKeydown(event: KeyboardEvent, option: SelectInputOption, index: number): void {
    switch (event.key) {
      case AccredibleKey.ARROW_UP:
        this._document.getElementById(`option-${index - 1}`)?.focus();
        break;

      case AccredibleKey.ARROW_DOWN:
        this._document.getElementById(`option-${index + 1}`)?.focus();
        break;

      case AccredibleKey.ENTER:
      case AccredibleKey.SPACE:
        this.sortChanged(option.value);
        break;
    }
  }

  private _detectMobileVersion(): void {
    this.isMobile.set(
      AccredibleResponsiveHelper.isScreenLessThan(AccredibleResponsiveBreakpoint.XS),
    );

    if (!this.isMobile()) {
      this._mobilePopUpService.close();
      this.mobilePopUpVisible.set(false);
    }
  }
}
